import { useSessionStorage } from '@vueuse/core'
import type { CustomerOrderResponse } from '~/rpcMethods'

type PrescriptionValuesFromOrder = {
  orderNumber: string | null
  rawPrescription: CustomerOrderResponse | null
  prescriptionValues: RxPrescriptionValues | null
  isProgressiveGlasses: boolean | null
  isConvertibleProgressiveGlasses: boolean | null
  progressiveGlassesPrescriptionValues: RxPrescriptionValues[]
}

const INITIAL_VALUES: PrescriptionValuesFromOrder = {
  orderNumber: null,
  rawPrescription: null,
  prescriptionValues: null,
  isProgressiveGlasses: null,
  isConvertibleProgressiveGlasses: null,
  progressiveGlassesPrescriptionValues: [],
}

const prescriptionValuesFromOrder = useSessionStorage(
  'prescriptionValuesFromOrder',
  INITIAL_VALUES,
)

export const usePrescriptionValuesFromOrder = (
  key = 'usePrescriptionValuesFromOrder',
) => {
  const {
    data,
    fetch: _fetch,
    fetching,
  } = useFimRpc('getPrescriptionValuesFromOrder', key)

  const scope = effectScope()
  tryOnScopeDispose(() => scope.stop())

  const isPrescriptionValuesFromOrderSet = computed(
    () =>
      prescriptionValuesFromOrder.value?.prescriptionValues?.left.pd !==
      undefined,
  )

  const fetch = async (params: Parameters<typeof _fetch>[0]) => {
    const orderNumber = (params?.orderNumber ?? '').replaceAll(' ', '')
    prescriptionValuesFromOrder.value.orderNumber = orderNumber
    await _fetch({ ...params, orderNumber })
    if (!data.value) {
      throw new Error('[usePrescriptionValuesFromOrder]: validation failed')
    }
  }

  const resetState = () => {
    prescriptionValuesFromOrder.value = INITIAL_VALUES
  }

  scope.run(() => {
    if (import.meta.server) {
      return
    }

    watch(data, (updatedPrescriptionValues) => {
      if (updatedPrescriptionValues) {
        prescriptionValuesFromOrder.value.rawPrescription =
          updatedPrescriptionValues
        prescriptionValuesFromOrder.value.prescriptionValues =
          isProgressiveGlassesCheck(updatedPrescriptionValues)
            ? null
            : formatPrescription(updatedPrescriptionValues)
        prescriptionValuesFromOrder.value.isProgressiveGlasses =
          isProgressiveGlassesCheck(updatedPrescriptionValues)
        prescriptionValuesFromOrder.value.isConvertibleProgressiveGlasses =
          isConvertibleProgressiveGlasses(updatedPrescriptionValues)
        prescriptionValuesFromOrder.value.progressiveGlassesPrescriptionValues =
          handleInitialProgressiveGlassesPrescription(updatedPrescriptionValues)
      }
    })
  })

  return {
    data: prescriptionValuesFromOrder,
    fetching,
    fetch,
    isPrescriptionValuesFromOrderSet,
    resetState,
  }
}
